import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { BrandIcon } from "@/components/BrandIcon";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, AreaChart, Area } from "recharts";
import { adminErrorDetail } from "../lib/adminError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTENT_KEYS = ["posts_posted", "videos_posted", "content_posted"];
const VIEW_KEYS = ["comment_views", "post_views", "views", "impressions", "views_impressions", "post_impressions"];
const ROWS_PER_PAGE = 30;
const VISIT_KEYS = ["website_visits"];
const visitKeysOf = (p) => (p.visit_keys && p.visit_keys.length ? p.visit_keys : ["website_visits"]);
const PLATFORM_COLORS = {
  reddit: "#FF4500",
  "youtube-longform": "#FF0000",
  "youtube-shorts": "#FF0000",
  tiktok: "#000000",
  instagram: "#E1306C",
  x: "#000000",
  discord: "#5865F2",
  "google-organic": "#4285F4",
  "direct-unknown": "#6B7280",
};
const cleanName = (n) => n.replace(/\s—\s/g, " ");

const DISTRO_ORDER = ["youtube-longform", "youtube-shorts", "reddit", "x", "tiktok", "instagram", "discord", "google-organic"];
const DISTRO_COLORS = {
  "youtube-longform": { type: "solid", color: "#A30000" },
  "youtube-shorts": { type: "solid", color: "#FF0000" },
  reddit: { type: "solid", color: "#FF4500" },
  x: { type: "solid", color: "#0A0A0A" },
  tiktok: { type: "gradient", stops: [["0%", "#25F4EE"], ["100%", "#FE2C55"]] },
  instagram: { type: "gradient", stops: [["0%", "#FEDA75"], ["35%", "#FA7E1E"], ["70%", "#D62976"], ["100%", "#962FBF"]] },
  discord: { type: "solid", color: "#5865F2" },
  "google-organic": { type: "gradient", stops: [["0%", "#4285F4"], ["50%", "#34A853"], ["100%", "#FBBC05"]] },
};

const DistroDonut = ({ pct, cfg, gid, testid }) => {
  const p = Math.max(0, Math.min(100, pct));
  const stroke = cfg.type === "gradient" ? `url(#${gid})` : cfg.color;
  return (
    <svg viewBox="0 0 36 36" className="w-14 h-14" data-testid={testid}>
      {cfg.type === "gradient" && (
        <defs>
          <linearGradient id={gid} x1="0%" y1="0%" x2="100%" y2="100%">
            {cfg.stops.map(([o, c]) => (
              <stop key={o} offset={o} stopColor={c} />
            ))}
          </linearGradient>
        </defs>
      )}
      <circle cx="18" cy="18" r="15.915" fill="none" stroke="#E5E5E5" strokeWidth="3.5" />
      {p > 0 && (
        <circle
          cx="18"
          cy="18"
          r="15.915"
          fill="none"
          stroke={stroke}
          strokeWidth="3.5"
          strokeDasharray={`${p} ${100 - p}`}
          strokeDashoffset="25"
          strokeLinecap="round"
        />
      )}
      <text x="18" y="18" textAnchor="middle" dominantBaseline="central" fontSize="7" fontWeight="600" fill="#0A0A0A">
        {`${Math.round(p)}%`}
      </text>
    </svg>
  );
};

const SHARE_REP_COLOR = {
  "youtube-longform": "#A30000",
  "youtube-shorts": "#FF0000",
  reddit: "#FF4500",
  x: "#0A0A0A",
  tiktok: "#FE2C55",
  instagram: "#D62976",
  discord: "#5865F2",
  "google-organic": "#4285F4",
};
const shareFillFor = (slug) => {
  const cfg = DISTRO_COLORS[slug];
  if (cfg && cfg.type === "gradient") return `url(#areaGrad-${slug})`;
  return (cfg && cfg.color) || "#0A0A0A";
};

const ShareTooltip = ({ active, payload, label, nameMap }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{ border: "1px solid #E5E5E5", background: "#fff", padding: "8px 10px", fontSize: 12 }}>
      <div style={{ fontWeight: 600, color: "#0A0A0A", marginBottom: 4 }}>Week of {label}</div>
      {[...payload].reverse().map((entry) => {
        const slug = entry.dataKey;
        const raw = (entry.payload && entry.payload[`${slug}_val`]) || 0;
        return (
          <div key={slug} style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
            <span style={{ color: SHARE_REP_COLOR[slug] || "#0A0A0A" }}>{nameMap[slug] || slug}</span>
            <span style={{ color: "#525252" }}>
              {Number(raw).toLocaleString("en-US")} · {Number(entry.value).toFixed(1)}%
            </span>
          </div>
        );
      })}
    </div>
  );
};

const HOW_TO_COUNT = {
  "youtube-longform": [
    "Record the current views shown by YouTube for the longform videos attributed to that posting date.",
    "Record website visits using the website's campaign/attribution data for YouTube for the same date.",
    "Because YouTube profile traffic cannot identify whether a visitor originally came from a Short or Longform video, use 10 Shorts views = 1 Longform view only as a manual estimate when analyzing the split; estimated visits are not actual visits.",
  ],
  "youtube-shorts": [
    "Record the current views shown by YouTube for the Shorts attributed to that posting date.",
    "Record website visits using the website's campaign/attribution data for YouTube for the same date.",
    "Because YouTube profile traffic cannot identify whether a visitor originally came from a Short or Longform video, use 10 Shorts views = 1 Longform view only as a manual estimate when analyzing the split; estimated visits are not actual visits.",
  ],
  tiktok: [
    "Record the current views shown by TikTok for the videos attributed to that posting date.",
    "Record website visits using the website's campaign/attribution data for TikTok for the same date.",
  ],
  instagram: [
    "Record the current views shown by Instagram for the videos attributed to that posting date.",
    "Record website visits using the website's campaign/attribution data for Instagram for the same date.",
  ],
  reddit: [
    "Record comments posted on the actual posting date. Three days later, manually check those comments and enter their current cumulative views in COMMENT VIEWS (3 DAYS LATER).",
    "Record website visits using the website's Reddit campaign/attribution data for the same measurement date.",
  ],
  x: [
    "Record posts and replies on their posting date, then enter cumulative impressions 3 days later.",
    "Record website visits from X campaign attribution for the same measurement date.",
  ],
  discord: [
    "Record the current member/new member figures shown by Discord for the selected date.",
    "Record website visits using the website's Discord campaign/attribution data.",
  ],
};

const pad = (n) => String(n).padStart(2, "0");
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const utcDayStr = (d) => d.toISOString().slice(0, 10);
const presetRange = (p) => {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  const localDay = (offset) => {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - offset);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };
  if (p === "today") return { from: localDay(0), to: localDay(0) };
  if (p === "yesterday") return { from: localDay(1), to: localDay(1) };
  if (p === "three_days_ago") return { from: localDay(3), to: localDay(3) };
  if (p === "this_week") {
    const dow = (now.getDay() + 6) % 7;
    const mon = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dow);
    return { from: `${mon.getFullYear()}-${pad(mon.getMonth() + 1)}-${pad(mon.getDate())}`, to: localDay(0) };
  }
  if (p === "this_month") return { from: utcDayStr(new Date(Date.UTC(y, m, 1))), to: "" };
  if (p === "last_month") return { from: utcDayStr(new Date(Date.UTC(y, m - 1, 1))), to: utcDayStr(new Date(Date.UTC(y, m, 0))) };
  if (p === "last_30") return { from: utcDayStr(new Date(now.getTime() - 29 * 86400000)), to: "" };
  if (p === "this_year") return { from: `${y}-01-01`, to: "" };
  return { from: "", to: "" };
};
const fmtUtcDay = (d) =>
  new Date(`${d}T00:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// Measurement reminder: Reddit & X record view/impression figures ~3 days after posting.
// Flag a date when content was posted but its matching figure is still blank (empty, not 0)
// and the date is at least 3 days old (the 3-day window has passed).
const MEASURE_PAIRS = {
  reddit: [["comments_posted", "comment_views"], ["posts_posted", "post_views"]],
  x: [["posts_posted", "post_impressions"], ["replies_posted", "reply_impressions"]],
};
const isBlankVal = (v) => v === undefined || v === null || v === "";
const daysOldUtc = (dateStr) => {
  const d = Date.parse(`${dateStr}T00:00:00Z`);
  if (Number.isNaN(d)) return 0;
  const today = Date.parse(`${new Date().toISOString().slice(0, 10)}T00:00:00Z`);
  return Math.floor((today - d) / 86400000);
};
const rowNeedsMeasure = (slug, values, dateStr) => {
  const pairs = MEASURE_PAIRS[slug];
  if (!pairs) return false;
  if (daysOldUtc(dateStr) < 3) return false;
  return pairs.some(([ck, vk]) => num(values[ck]) > 0 && isBlankVal(values[vk]));
};

// Google / Organic Search auto-populates from Dashboard acquisition data:
// replace its (unused) manual entries with synthesized per-day rows carrying
// only website_visits + book_downloads, so every existing aggregation picks it up.
const ACQ_SLUGS = ["google-organic", "direct-unknown"];
const mergeAcquisition = (platforms, entries, acq) => {
  if (!acq || typeof acq !== "object") return entries;
  let base = entries;
  const synth = [];
  ACQ_SLUGS.forEach((slug) => {
    const p = platforms.find((x) => x.slug === slug);
    const data = acq[slug];
    if (!p || !data || !Array.isArray(data.rows)) return;
    // Keep any manual daily rows the admin added for this acq platform (dated
    // corrections, negatives allowed) — only the auto rows are synthesized here.
    data.rows.forEach((r) => synth.push({
      id: `acq-${p.id}-${r.date}`,
      platform_id: p.id,
      date: r.date,
      values: { website_visits: r.visits, book_downloads: r.downloads },
      _acq: true,
    }));
    // The manual adjustment becomes a dated synthetic row so every date-bucketed
    // view counts it on its own date. Only Google/Organic keeps an adjustment;
    // Direct/Unknown is unattributed and carries no adjustment.
    const adj = data.adjustment;
    if (slug === "google-organic" && adj && adj.date && (num(adj.visits) !== 0 || num(adj.downloads) !== 0)) {
      synth.push({
        id: `adj-${p.id}`,
        platform_id: p.id,
        date: adj.date,
        values: { website_visits: num(adj.visits), book_downloads: num(adj.downloads) },
        _adj: true,
      });
    }
  });
  return [...base, ...synth];
};

export default function AdminTracking({ embedded = false, parentCsrf = "" }) {
  const [authed, setAuthed] = useState(null);
  const [csrf, setCsrf] = useState("");
  const [pw, setPw] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [platforms, setPlatforms] = useState([]);
  const [entries, setEntries] = useState([]);
  const [leadsCount, setLeadsCount] = useState(0);
  const [error, setError] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [preset, setPreset] = useState("all");
  const [addDates, setAddDates] = useState({});
  const [notices, setNotices] = useState({});
  const [drafts, setDrafts] = useState({});
  const [benchDrafts, setBenchDrafts] = useState({});
  const [collapsed, setCollapsed] = useState({});
  const [pages, setPages] = useState({});
  const [howToOpen, setHowToOpen] = useState({});
  const [insightsOpen, setInsightsOpen] = useState({});
  const [deleteRow, setDeleteRow] = useState(null);
  const [deletePlatform, setDeletePlatform] = useState(null);
  const [renaming, setRenaming] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [wowPeriod, setWowPeriod] = useState("week");
  const [chartPeriod, setChartPeriod] = useState("week");
  const [snapshotPeriod, setSnapshotPeriod] = useState("month");
  const [shareMetric, setShareMetric] = useState("visits");
  const [distPeriod, setDistPeriod] = useState("total");
  const [acqAdjust, setAcqAdjust] = useState({});
  const adjFor = (slug) => (acqAdjust[slug] && acqAdjust[slug].adjustment) || { visits: 0, downloads: 0, date: null, prev: null };
  const isAcqSlug = (slug) => slug === "google-organic" || slug === "direct-unknown";

  const fetchAll = useCallback(async () => {
    const [p, e, acq, lc] = await Promise.all([
      axios.get(`${API}/admin/tracking/platforms`, { withCredentials: true }),
      axios.get(`${API}/admin/tracking/entries`, { withCredentials: true }),
      axios.get(`${API}/admin/tracking/acquisition`, { withCredentials: true }),
      axios.get(`${API}/admin/tracking/leads-count`, { withCredentials: true }),
    ]);
    setPlatforms(p.data);
    setEntries(mergeAcquisition(p.data, e.data, acq.data));
    setAcqAdjust(acq.data || {});
    setLeadsCount(lc.data.total_unique_leads);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (embedded && parentCsrf) {
          setCsrf(parentCsrf);
          const [p, e, acq, lc] = await Promise.all([
            axios.get(`${API}/admin/tracking/platforms`, { withCredentials: true }),
            axios.get(`${API}/admin/tracking/entries`, { withCredentials: true }),
            axios.get(`${API}/admin/tracking/acquisition`, { withCredentials: true }),
            axios.get(`${API}/admin/tracking/leads-count`, { withCredentials: true }),
          ]);
          if (cancelled) return;
          setPlatforms(p.data);
          setEntries(mergeAcquisition(p.data, e.data, acq.data));
          setAcqAdjust(acq.data || {});
          setLeadsCount(lc.data.total_unique_leads);
          setAuthed(true);
          return;
        }
        const me = await axios.get(`${API}/admin/me`, { withCredentials: true });
        if (cancelled) return;
        setCsrf(me.data.csrf_token);
        setAuthed(true);
        await fetchAll();
      } catch {
        if (!cancelled) setAuthed(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchAll, embedded, parentCsrf]);

  const login = async (e) => {
    e.preventDefault();
    if (!pw) return;
    setLoginLoading(true);
    setLoginError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { password: pw }, { withCredentials: true });
      setCsrf(res.data.csrf_token);
      setAuthed(true);
      setPw("");
      await fetchAll();
    } catch (err) {
      setLoginError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Something went wrong.");
    } finally {
      setLoginLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, { withCredentials: true });
    } catch {
      // ignore
    }
    setAuthed(false);
    setCsrf("");
    setPlatforms([]);
    setEntries([]);
  };

  const call = async (method, path, body) => {
    setError("");
    try {
      const res = await axios.request({
        method,
        url: `${API}${path}`,
        data: body,
        headers: { "X-CSRF-Token": csrf },
        withCredentials: true,
      });
      return res.data;
    } catch (err) {
      if (err.response?.status === 401) {
        setAuthed(false);
        return null;
      }
      setError(adminErrorDetail(err, "Action failed."));
      return null;
    }
  };

  if (authed === null) {
    return embedded ? (
      <p className="text-muted" data-testid="admin-tracking-loading">Loading…</p>
    ) : (
      <div className="min-h-screen flex items-center justify-center bg-paper" data-testid="admin-tracking-loading">
        <p className="text-muted">Loading…</p>
      </div>
    );
  }

  if (!authed) {
    if (embedded) {
      return (
        <p className="text-sm text-muted" data-testid="admin-tracking-session-expired">
          Tracking session expired — reload the Admin page to sign in again.
        </p>
      );
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper px-6" data-testid="admin-tracking-login">
        <form onSubmit={login} className="w-full max-w-sm">
          <div className="label-mono text-muted">Distribution</div>
          <h1 className="font-display text-4xl md:text-5xl mt-6 mb-10 leading-tight">Enter password.</h1>
          <input
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="Password"
            className="w-full bg-transparent border-b border-ink py-3 text-lg outline-none"
            data-testid="admin-tracking-password-input"
            autoFocus
          />
          {loginError && (
            <p className="mt-4 text-sm text-red-600" data-testid="admin-tracking-login-error">{loginError}</p>
          )}
          <button
            type="submit"
            disabled={loginLoading}
            className="mt-8 w-full bg-ink text-white py-4 label-mono hover:bg-leaf transition-colors disabled:opacity-60"
            data-testid="admin-tracking-login-submit"
          >
            {loginLoading ? "Checking…" : "Enter"}
          </button>
        </form>
      </div>
    );
  }

  const invalid = Boolean(from && to && from > to);
  const rFrom = invalid ? "" : from;
  const rTo = invalid ? "" : to;
  const rangeActive = Boolean(rFrom || rTo);
  const inRange = (d) => {
    if (!rangeActive) return true;
    if (!d) return false;
    if (rFrom && d < rFrom) return false;
    if (rTo && d > rTo) return false;
    return true;
  };
  const rangeLabel = !rangeActive
    ? "All time"
    : rFrom && rTo
      ? `${fmtUtcDay(rFrom)} – ${fmtUtcDay(rTo)}`
      : rFrom
        ? `From ${fmtUtcDay(rFrom)}`
        : `Through ${fmtUtcDay(rTo)}`;

  const rowsFor = (pid) =>
    entries.filter((e) => e.platform_id === pid && inRange(e.date)).sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));

  // Direct/Unknown is unattributed traffic: excluded from every chart, share,
  // leaderboard, weekly and monthly calculation. It only appears as a single
  // raw summary row at the bottom of the platform table.
  const duPlatform = platforms.find((p) => p.slug === "direct-unknown") || null;
  const duId = duPlatform ? duPlatform.id : null;
  const attributed = platforms.filter((p) => p.slug !== "direct-unknown");

  const setNotice = (pid, msg) => setNotices((prev) => ({ ...prev, [pid]: msg }));

  const saveCell = async (platform, entry, key, raw) => {
    const res = await call("put", "/admin/tracking/entries", {
      platform_id: platform.id,
      date: entry.date,
      values: { [key]: raw },
    });
    if (res) {
      setEntries((prev) => prev.map((e) => (e.id === res.id ? res : e)));
    }
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[`${entry.id}:${key}`];
      return next;
    });
  };

  const addRow = async (platform) => {
    const date = addDates[platform.id] || todayStr();
    // Only a manual row blocks a duplicate; auto (_acq) and lump (_adj) rows don't,
    // so a dated correction can be added on a date that already has auto traffic.
    const existing = entries.find((e) => e.platform_id === platform.id && e.date === date && !e._acq && !e._adj);
    if (existing) {
      setNotice(
        platform.id,
        inRange(date)
          ? `${fmtUtcDay(date)} already has a row — edit it below.`
          : `${fmtUtcDay(date)} already has a row, but it falls outside the selected period.`
      );
      return;
    }
    const res = await call("put", "/admin/tracking/entries", { platform_id: platform.id, date, values: {} });
    if (res) {
      setEntries((prev) => [res, ...prev]);
      setNotice(platform.id, inRange(date) ? "" : `${fmtUtcDay(date)} falls outside the selected period.`);
    }
  };

  const confirmDeleteRow = async () => {
    const target = deleteRow;
    setDeleteRow(null);
    const res = await call("delete", `/admin/tracking/entries/${target}`);
    if (res) setEntries((prev) => prev.filter((e) => e.id !== target));
  };

  const confirmDeletePlatform = async () => {
    const target = deletePlatform;
    setDeletePlatform(null);
    const res = await call("delete", `/admin/tracking/platforms/${target}`);
    if (res) {
      setPlatforms((prev) => prev.filter((p) => p.id !== target));
      setEntries((prev) => prev.filter((e) => e.platform_id !== target));
    }
  };

  const saveRename = async (platform) => {
    const res = await call("patch", `/admin/tracking/platforms/${platform.id}`, { name: renameValue });
    if (res) {
      setPlatforms((prev) => prev.map((p) => (p.id === res.id ? res : p)));
      setRenaming(null);
      setRenameValue("");
    }
  };

  const saveBenchmark = async (platform, raw) => {
    const trimmed = String(raw).trim();
    const res = await call("patch", `/admin/tracking/platforms/${platform.id}/benchmark`, {
      benchmark_pct: trimmed === "" ? null : Number(trimmed),
    });
    if (res) setPlatforms((prev) => prev.map((p) => (p.id === res.id ? res : p)));
    setBenchDrafts((prev) => {
      const next = { ...prev };
      delete next[platform.id];
      return next;
    });
  };

  const benchmarkFor = (platform, rows) => {
    const keys = platform.benchmark_keys && platform.benchmark_keys.length
      ? platform.benchmark_keys
      : platform.benchmark_source
        ? [platform.benchmark_source]
        : [];
    if (!keys.length) return null;
    const label = platform.benchmark_label
      || keys.map((k) => { const c = platform.columns.find((x) => x.key === k); return c ? c.label : k; }).join(" + ");
    const sourceTotal = keys.reduce((t, k) => t + rows.reduce((s, r) => s + num(r.values[k]), 0), 0);
    const actual = visitKeysOf(platform).reduce((t, k) => t + rows.reduce((s, r) => s + num(r.values[k]), 0), 0);
    const pct = platform.benchmark_pct;
    const expected = pct === null || pct === undefined ? null : sourceTotal * (pct / 100);
    return {
      sourceLabel: label,
      actual,
      expected,
      diff: expected === null ? null : actual - expected,
      perf: expected !== null && expected > 0 ? ((actual - expected) / expected) * 100 : null,
      under: expected !== null && expected > 0 && (actual / expected) * 100 < 90,
    };
  };

  const createPlatform = async () => {
    const res = await call("post", "/admin/tracking/platforms", { name: newName });
    if (res) {
      setPlatforms((prev) => [...prev, res]);
      setAdding(false);
      setNewName("");
    }
  };

  const saveAdj = async (slug, patch) => {
    const cur = adjFor(slug);
    const body = {
      slug,
      visits: patch.visits !== undefined ? patch.visits : cur.visits,
      downloads: patch.downloads !== undefined ? patch.downloads : cur.downloads,
      date: patch.date !== undefined ? patch.date : cur.date,
    };
    const res = await call("put", "/admin/tracking/acquisition/adjust", body);
    if (res) await fetchAll();
  };

  const restorePrev = async (slug) => {
    const res = await call("post", "/admin/tracking/acquisition/restore-previous", { slug });
    if (res) { setError(""); await fetchAll(); }
  };

  const totalsFor = (platform, rows) => {
    const latestDate = rows.length ? rows[0].date : null;
    const out = {};
    platform.columns.forEach((c) => {
      if (c.computed) {
        const nSum = c.computed.numerator.reduce((t, k) => t + rows.reduce((s, r) => s + num(r.values[k]), 0), 0);
        const dSum = c.computed.denominator.reduce((t, k) => t + rows.reduce((s, r) => s + num(r.values[k]), 0), 0);
        out[c.key] = dSum > 0 ? (nSum / dSum).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : "—";
      } else if (c.total_rule === "none") {
        out[c.key] = "—";
      } else if (c.total_rule === "latest") {
        const row = rows.find((r) => r.date === latestDate);
        const v = row ? row.values[c.key] : "";
        out[c.key] = v === "" || v === undefined || v === null ? "—" : num(v).toLocaleString("en-US");
      } else {
        out[c.key] = rows.reduce((s, r) => s + num(r.values[c.key]), 0).toLocaleString("en-US");
      }
    });
    return out;
  };

  const weekly = (() => {
    const now = new Date();
    const p2 = (n) => String(n).padStart(2, "0");
    const dayStr = (d) => `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
    let curStart, curEnd, prevStart;
    if (wowPeriod === "month") {
      curStart = new Date(now.getFullYear(), now.getMonth(), 1);
      curEnd = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      prevStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    } else if (wowPeriod === "quarter") {
      const q = Math.floor(now.getMonth() / 3);
      curStart = new Date(now.getFullYear(), q * 3, 1);
      curEnd = new Date(now.getFullYear(), q * 3 + 3, 1);
      prevStart = new Date(now.getFullYear(), q * 3 - 3, 1);
    } else {
      const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - ((now.getDay() + 6) % 7));
      curStart = monday;
      curEnd = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 7);
      prevStart = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() - 7);
    }
    const aggOne = (p, from, toExclusive) => {
      const rows = entries.filter((e) => e.platform_id === p.id && e.date >= from && e.date < toExclusive);
      let v = 0;
      let c = 0;
      let d = 0;
      p.columns.forEach((col) => {
        const sum = rows.reduce((s, r) => s + num(r.values[col.key]), 0);
        if (VISIT_KEYS.includes(col.key)) v += sum;
        else if (col.key === "book_downloads") d += sum;
        else if (CONTENT_KEYS.includes(col.key)) c += sum;
      });
      return { visits: v, content: c, downloads: d };
    };
    const thisStart = dayStr(curStart);
    const perPlatform = attributed.map((p) => ({
      name: p.name,
      slug: p.slug,
      format: p.format,
      cur: aggOne(p, thisStart, dayStr(curEnd)),
      prev: aggOne(p, dayStr(prevStart), thisStart),
    }));
    const sumOn = (key, field) => perPlatform.reduce((s, x) => s + x[key][field], 0);
    return {
      cur: { visits: sumOn("cur", "visits"), content: sumOn("cur", "content"), downloads: sumOn("cur", "downloads") },
      prev: { visits: sumOn("prev", "visits"), content: sumOn("prev", "content"), downloads: sumOn("prev", "downloads") },
      perPlatform,
      weekStart: thisStart,
    };
  })();

  const weeklyChart = (() => {
    const p2 = (n) => String(n).padStart(2, "0");
    const bucketOf = (dateStr) => {
      const [y, m, d] = dateStr.split("-").map(Number);
      if (chartPeriod === "month") {
        return { key: `${y}-${p2(m)}`, label: new Date(y, m - 1, 1).toLocaleDateString("en-GB", { month: "short", year: "numeric" }) };
      }
      if (chartPeriod === "quarter") {
        const q = Math.floor((m - 1) / 3) + 1;
        return { key: `${y}-Q${q}`, label: `Q${q} ${y}` };
      }
      const dt = new Date(y, m - 1, d);
      const mon = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate() - ((dt.getDay() + 6) % 7));
      const key = `${mon.getFullYear()}-${p2(mon.getMonth() + 1)}-${p2(mon.getDate())}`;
      return { key, label: mon.toLocaleDateString("en-GB", { day: "numeric", month: "short" }) };
    };
    const map = new Map();
    entries.forEach((e) => {
      if (!inRange(e.date)) return;
      if (e.platform_id === duId) return;
      const { key, label } = bucketOf(e.date);
      const acc = map.get(key) || { label, visits: 0, downloads: 0 };
      acc.visits += num(e.values.website_visits);
      acc.downloads += num(e.values.book_downloads);
      map.set(key, acc);
    });
    const rows = [...map.entries()]
      .sort((a, b) => (a[0] < b[0] ? -1 : 1))
      .map(([key, v]) => ({ week: key, label: v.label, visits: v.visits, downloads: v.downloads }));
    const avg = rows.length ? Math.round(rows.reduce((s, r) => s + r.visits, 0) / rows.length) : 0;
    return { rows, avg };
  })();

  const streak = (() => {
    const days = new Set();
    platforms.forEach((p) => {
      const contentKeys = p.columns.filter((c) => !c.computed && CONTENT_KEYS.includes(c.key)).map((c) => c.key);
      if (!contentKeys.length) return;
      entries
        .filter((e) => e.platform_id === p.id)
        .forEach((e) => {
          if (contentKeys.some((k) => num(e.values[k]) > 0)) days.add(e.date);
        });
    });
    const p2 = (n) => String(n).padStart(2, "0");
    const dayStr = (d) => `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
    const now = new Date();
    let cursor = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (!days.has(dayStr(cursor))) cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() - 1);
    let count = 0;
    while (days.has(dayStr(cursor)) && count < 3650) {
      count += 1;
      cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() - 1);
    }
    const sorted = [...days].sort();
    let longest = 0;
    let run = 0;
    let prevDate = null;
    sorted.forEach((d) => {
      const cur = new Date(`${d}T00:00:00Z`);
      run = prevDate && (cur - prevDate) / 86400000 === 1 ? run + 1 : 1;
      if (run > longest) longest = run;
      prevDate = cur;
    });
    return { current: count, longest };
  })();

  const monthly = (() => {
    const map = new Map();
    attributed.forEach((p) => {
      entries
        .filter((e) => e.platform_id === p.id)
        .forEach((e) => {
          const ym = e.date.slice(0, 7);
          const acc = map.get(ym) || { content: 0, views: 0, visits: 0, downloads: 0 };
          p.columns.forEach((c) => {
            const v = num(e.values[c.key]);
            if (VISIT_KEYS.includes(c.key)) acc.visits += v;
            else if (c.key === "book_downloads") acc.downloads += v;
            else if (CONTENT_KEYS.includes(c.key)) acc.content += v;
            else if (VIEW_KEYS.includes(c.key)) acc.views += v;
          });
          map.set(ym, acc);
        });
    });
    return [...map.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
  })();

  const yearly = (() => {
    const map = new Map();
    attributed.forEach((p) => {
      entries
        .filter((e) => e.platform_id === p.id)
        .forEach((e) => {
          const y = e.date.slice(0, 4);
          const acc = map.get(y) || { content: 0, views: 0, visits: 0, downloads: 0 };
          p.columns.forEach((c) => {
            const v = num(e.values[c.key]);
            if (VISIT_KEYS.includes(c.key)) acc.visits += v;
            else if (c.key === "book_downloads") acc.downloads += v;
            else if (CONTENT_KEYS.includes(c.key)) acc.content += v;
            else if (VIEW_KEYS.includes(c.key)) acc.views += v;
          });
          map.set(y, acc);
        });
    });
    return [...map.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
  })();
  const overview = (() => {
    let visits = 0;
    let downloads = 0;
    let content = 0;
    let views = 0;
    let commentsPosted = 0;
    let commentViews = 0;
    let postsPosted = 0;
    let postViews = 0;
    let shortform = 0;
    let longform = 0;
    let shortViews = 0;
    let longViews = 0;
    let shortVisits = 0;
    let longVisits = 0;
    let textViews = 0;
    let textVisits = 0;
    attributed.forEach((p) => {
      const rows = rowsFor(p.id);
      const sumOf = (key) => rows.reduce((s, r) => s + num(r.values[key]), 0);
      p.columns.forEach((c) => {
        const sum = rows.reduce((s, r) => s + num(r.values[c.key]), 0);
        if (c.key === "website_visits") visits += sum;
        else if (c.key === "book_downloads") downloads += sum;
        else if (CONTENT_KEYS.includes(c.key)) content += sum;
        else if (VIEW_KEYS.includes(c.key)) views += sum;
        if (VISIT_KEYS.includes(c.key) && c.key !== "website_visits") visits += sum;
        if (c.key === "comments_posted") commentsPosted += sum;
        else if (c.key === "comment_views") commentViews += sum;
        else if (c.key === "posts_posted") postsPosted += sum;
        else if (c.key === "post_views" || c.key === "impressions" || c.key === "post_impressions") postViews += sum;
        else if (c.key === "videos_posted") {
          if (p.format === "longform") longform += sum;
          else shortform += sum;
        }
      });
      const keys = p.columns.map((c) => c.key);
      const pViews = p.columns.filter((c) => VIEW_KEYS.includes(c.key)).reduce((t, c) => t + sumOf(c.key), 0);
      const pVisits = keys.includes("website_visits") ? sumOf("website_visits") : 0;
      if (p.format === "longform") {
        longViews += pViews;
        longVisits += pVisits;
      } else if (p.format === "shortform") {
        shortViews += pViews;
        shortVisits += pVisits;
      }
      const textKeys = ["comment_views", "post_views", "post_impressions"].filter((k) => keys.includes(k));
      if (textKeys.length) {
        textViews += textKeys.reduce((t, k) => t + sumOf(k), 0);
        textVisits += pVisits;
      }
    });
    // The two auto-filled acquisition platforms include their manual adjustment as
    // a dated row inside `entries` (see mergeAcquisition), so rowsFor already counts
    // it here — no separate, clamped add-back is needed.
    const per1k = (n) => (views > 0 ? ((n / views) * 1000).toFixed(1) : "—");
    const per1kOf = (n, d) => (d > 0 ? ((n / d) * 1000).toFixed(1) : "—");
    return {
      visits: visits.toLocaleString("en-US"),
      visitsNum: visits,
      downloads: downloads.toLocaleString("en-US"),
      content: content.toLocaleString("en-US"),
      views: views.toLocaleString("en-US"),
      commentsPosted: commentsPosted.toLocaleString("en-US"),
      commentViews: commentViews.toLocaleString("en-US"),
      postsPosted: postsPosted.toLocaleString("en-US"),
      postViews: postViews.toLocaleString("en-US"),
      shortform: shortform.toLocaleString("en-US"),
      longform: longform.toLocaleString("en-US"),
      shortViews: shortViews.toLocaleString("en-US"),
      longViews: longViews.toLocaleString("en-US"),
      visitRate: per1k(visits),
      downloadRate: per1k(downloads),
      downloadsPerContent: content > 0 ? (downloads / content).toFixed(2) : "—",
      contentPerDownload: downloads > 0 ? (content / downloads).toFixed(1) : "—",
      shortformRate: per1kOf(shortVisits, shortViews),
      shortformRate10k: shortViews > 0 ? ((shortVisits / shortViews) * 10000).toFixed(1) : "—",
      textRate: per1kOf(textVisits, textViews),
      longformRate: per1kOf(longVisits, longViews),
    };
  })();

  const DOWNLOAD_PLATFORM_ORDER = ["youtube-longform", "youtube-shorts", "tiktok", "instagram", "reddit", "x", "google-organic"];
  const downloadsByPlatform = platforms
    .filter((p) => DOWNLOAD_PLATFORM_ORDER.includes(p.slug))
    .sort((a, b) => DOWNLOAD_PLATFORM_ORDER.indexOf(a.slug) - DOWNLOAD_PLATFORM_ORDER.indexOf(b.slug))
    .map((p) => {
      const rows = rowsFor(p.id);
      const downloads = rows.reduce((s, r) => s + num(r.values.book_downloads), 0);
      const visits = rows.reduce((s, r) => s + num(r.values.website_visits), 0);
      const contentKeys = p.columns.filter((c) => !c.computed && CONTENT_KEYS.includes(c.key)).map((c) => c.key);
      const content = contentKeys.reduce((t, k) => t + rows.reduce((s, r) => s + num(r.values[k]), 0), 0);
      const views = p.columns
        .filter((c) => VIEW_KEYS.includes(c.key))
        .reduce((t, c) => t + rows.reduce((s, r) => s + num(r.values[c.key]), 0), 0);
      return {
        slug: p.slug,
        name: p.name,
        content,
        downloads,
        visits,
        views,
        ratePerVisits: visits > 0 ? ((downloads / visits) * 1000).toFixed(1) : "—",
        rate: views > 0 ? ((downloads / views) * 1000).toFixed(1) : "—",
      };
    });

  const platformDistribution = (() => {
    const now = new Date();
    const p2 = (n) => String(n).padStart(2, "0");
    const ds = (d) => `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
    let curStart = null;
    let curEnd = null;
    if (distPeriod === "week") {
      const mon = new Date(now.getFullYear(), now.getMonth(), now.getDate() - ((now.getDay() + 6) % 7));
      curStart = ds(mon);
      curEnd = ds(new Date(mon.getFullYear(), mon.getMonth(), mon.getDate() + 7));
    } else if (distPeriod === "month") {
      curStart = ds(new Date(now.getFullYear(), now.getMonth(), 1));
      curEnd = ds(new Date(now.getFullYear(), now.getMonth() + 1, 1));
    } else if (distPeriod === "year") {
      curStart = ds(new Date(now.getFullYear(), 0, 1));
      curEnd = ds(new Date(now.getFullYear() + 1, 0, 1));
    }
    const rowsOf = (pid) =>
      distPeriod === "total"
        ? rowsFor(pid)
        : entries.filter((e) => e.platform_id === pid && e.date >= curStart && e.date < curEnd);
    const items = DISTRO_ORDER.map((slug) => platforms.find((p) => p.slug === slug))
      .filter(Boolean)
      .map((p) => {
        const rows = rowsOf(p.id);
        const visits = rows.reduce((s, r) => s + num(r.values.website_visits), 0);
        const downloads = rows.reduce((s, r) => s + num(r.values.book_downloads), 0);
        return { slug: p.slug, name: p.name, visits, downloads };
      });
    const totalVisits = items.reduce((s, x) => s + x.visits, 0);
    const totalDownloads = items.reduce((s, x) => s + x.downloads, 0);
    return { items, totalVisits, totalDownloads };
  })();

  const platformShareSeries = (() => {
    const present = DISTRO_ORDER.map((slug) => platforms.find((p) => p.slug === slug)).filter(Boolean);
    const slugs = present.map((p) => p.slug);
    const nameMap = {};
    present.forEach((p) => { nameMap[p.slug] = cleanName(p.name); });
    const idToSlug = {};
    present.forEach((p) => { idToSlug[p.id] = p.slug; });
    const idToContentKeys = {};
    present.forEach((p) => {
      idToContentKeys[p.id] = p.columns.filter((c) => !c.computed && CONTENT_KEYS.includes(c.key)).map((c) => c.key);
    });
    const p2 = (n) => String(n).padStart(2, "0");
    const mondayOf = (dateStr) => {
      const [y, m, d] = dateStr.split("-").map(Number);
      const dt = new Date(y, m - 1, d);
      const mon = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate() - ((dt.getDay() + 6) % 7));
      const key = `${mon.getFullYear()}-${p2(mon.getMonth() + 1)}-${p2(mon.getDate())}`;
      return { key, label: mon.toLocaleDateString("en-GB", { day: "numeric", month: "short" }) };
    };
    const weekMap = new Map();
    entries.forEach((e) => {
      if (!inRange(e.date)) return;
      const slug = idToSlug[e.platform_id];
      if (!slug) return;
      const { key, label } = mondayOf(e.date);
      const acc = weekMap.get(key) || { label, v: {} };
      const cur = acc.v[slug] || { visits: 0, downloads: 0, content: 0 };
      cur.visits += num(e.values.website_visits);
      cur.downloads += num(e.values.book_downloads);
      cur.content += (idToContentKeys[e.platform_id] || []).reduce((s, k) => s + num(e.values[k]), 0);
      acc.v[slug] = cur;
      weekMap.set(key, acc);
    });
    const rows = [...weekMap.entries()]
      .sort((a, b) => (a[0] < b[0] ? -1 : 1))
      .map(([key, acc]) => {
        const row = { week: key, label: acc.label };
        const total = slugs.reduce((s, sl) => s + (acc.v[sl] ? acc.v[sl][shareMetric] : 0), 0);
        slugs.forEach((sl) => {
          const val = acc.v[sl] ? acc.v[sl][shareMetric] : 0;
          row[sl] = total > 0 ? (val / total) * 100 : 0;
          row[`${sl}_val`] = val;
        });
        return row;
      });
    return { rows, slugs, nameMap };
  })();

  const conversionLeaderboard = (() => {
    const items = DISTRO_ORDER.map((slug) => platforms.find((p) => p.slug === slug))
      .filter(Boolean)
      .map((p) => {
        const rows = rowsFor(p.id);
        const visits = rows.reduce((s, r) => s + num(r.values.website_visits), 0);
        const downloads = rows.reduce((s, r) => s + num(r.values.book_downloads), 0);
        const rate = visits > 0 ? (downloads / visits) * 1000 : null;
        return { slug: p.slug, name: cleanName(p.name), visits, downloads, rate };
      });
    const ranked = items.slice().sort((a, b) => {
      if (a.rate === null && b.rate === null) return 0;
      if (a.rate === null) return 1;
      if (b.rate === null) return -1;
      return b.rate - a.rate;
    });
    const maxRate = ranked.reduce((m, x) => (x.rate !== null && x.rate > m ? x.rate : m), 0);
    return { ranked, maxRate };
  })();

  const metric = (label, value, testid, valueClass, bold) => (
    <div key={testid} className="py-3 flex items-baseline justify-between gap-4" data-testid={testid}>
      <span className={`text-sm ${bold ? "text-leaf font-semibold" : "text-ink"}`}>{label}</span>
      <span className={`font-display ${bold ? "text-2xl text-leaf" : "text-xl"} ${valueClass || ""}`}>{value}</span>
    </div>
  );

  const oneDec = (v) => v.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

  const exportCsv = (filename, headers, rows) => {
    const esc = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [headers.join(","), ...rows.map((r) => r.map(esc).join(","))];
    const blob = new Blob(["\uFEFF" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const exportTrackingCsv = () => {
    const gen = new Date().toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
    const plain = (v) => (v === "—" || v === null || v === undefined ? "" : String(v).replace(/,/g, ""));
    const out = [["", "Period", "", "Selected range", rangeLabel]];
    [
      ["Total comments posted", overview.commentsPosted],
      ["Total shortform videos posted", overview.shortform],
      ["Total longform videos posted", overview.longform],
      ["Total posts posted", overview.postsPosted],
      ["Total content posted", overview.content],
      ["Total comment views", overview.commentViews],
      ["Total post views / impressions", overview.postViews],
      ["Total website visits", overview.visits],
      ["Total book downloads", overview.downloads],
      ["Total views / impressions", overview.views],
      ["Website visits per 1,000 views", overview.visitRate],
      ["Shortform website visits per 1,000 views", overview.shortformRate],
      ["Longform website visits per 1,000 views", overview.longformRate],
    ].forEach(([m, v]) => out.push(["", "Overview", "", m, plain(v)]));
    monthly.forEach(([ym, m]) => {
      out.push(["", "Monthly", ym, "Content posted", m.content]);
      out.push(["", "Monthly", ym, "Views / impressions", m.views]);
      out.push(["", "Monthly", ym, "Website visits", m.visits]);
      out.push(["", "Monthly", ym, "Book downloads", m.downloads]);
      out.push(["", "Monthly", ym, "Website visits per 1,000 views", m.views > 0 ? ((m.visits / m.views) * 1000).toFixed(1) : ""]);
    });
    downloadsByPlatform.forEach((d) => {
      out.push([cleanName(d.name), "Acquisition", "", "Website visits", d.visits]);
      out.push([cleanName(d.name), "Acquisition", "", "Book downloads", d.downloads]);
      out.push([cleanName(d.name), "Acquisition", "", "Book downloads per 1,000 visits", d.ratePerVisits === "—" ? "" : d.ratePerVisits]);
    });
    platforms.forEach((p) => {
      const rows = rowsFor(p.id);
      const totals = totalsFor(p, rows);
      rows.forEach((r) => {
        p.columns.forEach((c) => {
          if (c.computed) {
            const nSum = c.computed.numerator.reduce((s, k) => s + num(r.values[k]), 0);
            const dSum = c.computed.denominator.reduce((s, k) => s + num(r.values[k]), 0);
            out.push([p.name, "Daily", r.date, c.label, dSum > 0 ? (nSum / dSum).toFixed(1) : ""]);
            return;
          }
          const v = r.values[c.key];
          out.push([p.name, "Daily", r.date, c.label, v === undefined || v === null ? "" : plain(v)]);
        });
      });
      p.columns.forEach((c) => out.push([p.name, "Total", "", c.label, plain(totals[c.key])]));
      const bench = benchmarkFor(p, rows);
      if (bench) {
        out.push([p.name, "Benchmark", "", `Benchmark % of ${bench.sourceLabel}`, p.benchmark_pct === null || p.benchmark_pct === undefined ? "" : p.benchmark_pct]);
        out.push([p.name, "Benchmark", "", "Actual website visits", bench.actual]);
        out.push([p.name, "Benchmark", "", "Expected website visits", bench.expected === null ? "" : bench.expected]);
        out.push([p.name, "Benchmark", "", "Difference vs expected", bench.diff === null ? "" : bench.diff]);
        out.push([p.name, "Benchmark", "", "Performance vs expected", bench.perf === null ? "" : bench.perf]);
      }
    });
    const stamp = todayStr();
    const name = rangeActive
      ? `biology-trader-tracking-${stamp}-${rFrom || "start"}-to-${rTo || "now"}.csv`
      : `biology-trader-tracking-${stamp}.csv`;
    exportCsv(name, ["PLATFORM", "SECTION", "DATE", "METRIC", "VALUE", "GENERATED_AT"], out.map((r) => [...r, gen]));
  };

  const gridStyle = (n) => ({ gridTemplateColumns: `minmax(110px, 1.2fr) repeat(${n}, minmax(112px, 1fr)) 96px` });
  const btn = "label-mono border border-hairline px-3 py-1.5 hover:bg-ink hover:text-white transition-colors";
  const btnDanger = "label-mono border border-red-200 text-red-600 px-3 py-1.5 hover:bg-red-600 hover:text-white transition-colors";

  const content = (
    <div data-testid="admin-tracking-panel">
        <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
          <h2 className="font-display text-3xl md:text-4xl">Distribution</h2>
          <button
            onClick={exportTrackingCsv}
            className="label-mono border border-hairline text-ink px-3 py-1.5 hover:bg-ink hover:text-white transition-colors"
            data-testid="tracking-export-csv"
          >
            Export CSV
          </button>
        </div>
        {error && <p className="mb-6 text-sm text-red-600" data-testid="tracking-action-error">{error}</p>}

        <div className="mb-8 border border-hairline p-4" data-testid="tracking-range-control">
          <div className="flex flex-wrap items-end gap-4">
            <label className="label-mono text-muted flex items-center gap-2">
              From
              <input
                type="date"
                value={from}
                onChange={(e) => { setFrom(e.target.value); setPreset("custom"); }}
                className="bg-transparent border-b border-ink py-1 outline-none text-ink"
                data-testid="tracking-range-from"
              />
            </label>
            <label className="label-mono text-muted flex items-center gap-2">
              To
              <input
                type="date"
                value={to}
                onChange={(e) => { setTo(e.target.value); setPreset("custom"); }}
                className="bg-transparent border-b border-ink py-1 outline-none text-ink"
                data-testid="tracking-range-to"
              />
            </label>
            <div className="flex flex-wrap gap-1">
              {[["all", "All time"], ["this_week", "This week"], ["this_month", "This month"], ["last_month", "Last month"], ["last_30", "Last 30 days"], ["this_year", "This year"], ["today", "Today"], ["yesterday", "Yesterday"], ["three_days_ago", "3 days ago"]].map(([p, lbl]) => (
                <button
                  key={p}
                  onClick={() => { const r = presetRange(p); setFrom(r.from); setTo(r.to); setPreset(p); }}
                  className={`label-mono px-3 py-1.5 border transition-colors ${preset === p ? "bg-ink text-white border-ink" : "border-hairline text-ink hover:bg-surface"}`}
                  data-testid={`tracking-preset-${p}`}
                >
                  {lbl}
                </button>
              ))}
            </div>
          </div>
          {invalid ? (
            <p className="mt-3 text-sm text-red-600" data-testid="tracking-range-invalid">
              FROM is later than TO — fix the dates. Showing all time until then.
            </p>
          ) : (
            <p className="mt-3 label-mono text-muted" data-testid="tracking-range-active">Showing: {rangeLabel}</p>
          )}
        </div>

        <div className="mb-10 max-w-2xl" data-testid="tracking-overview">
          <h3 className="label-mono text-muted mb-6">Overview</h3>

          <div className="mb-8" data-testid="tracking-overview-totals">
            <div className="border-t border-hairline divide-y divide-hairline">
              {metric("Total website visits", overview.visits, "tracking-overview-total-visits", "", true)}
              {metric("Total unique ebook leads", leadsCount.toLocaleString("en-US"), "tracking-overview-total-leads", "", true)}
              {metric("Total ebook downloads", overview.downloads, "tracking-overview-total-downloads", "", true)}
            </div>
          </div>

          <div className="mb-8" data-testid="tracking-overview-content">
            <h4 className="label-mono text-muted mb-3">Content</h4>
            <div className="border-t border-hairline divide-y divide-hairline">
              {metric("Total comments posted", overview.commentsPosted, "tracking-total-comments-posted")}
              {metric("Total shortform videos posted", overview.shortform, "tracking-total-shortform")}
              {metric("Total longform videos posted", overview.longform, "tracking-total-longform")}
              {metric("Total posts posted", overview.postsPosted, "tracking-total-posts-posted")}
              {metric("Total content posted", overview.content, "tracking-total-content", "", true)}
            </div>
          </div>

          <div className="mb-8" data-testid="tracking-overview-reach">
            <h4 className="label-mono text-muted mb-3">Reach</h4>
            <div className="border-t border-hairline divide-y divide-hairline">
              {metric("Total comment views", overview.commentViews, "tracking-total-comment-views")}
              {metric("Total shortform views", overview.shortViews, "tracking-total-shortform-views")}
              {metric("Total longform views", overview.longViews, "tracking-total-longform-views")}
              {metric("Total post views / impressions", overview.postViews, "tracking-total-post-views")}
              {metric("Total views / impressions", overview.views, "tracking-total-views", "", true)}
            </div>
          </div>

          <div className="mb-8" data-testid="tracking-overview-conversion">
            <h4 className="label-mono text-muted mb-3">Conversion</h4>
            <div className="border-t border-hairline divide-y divide-hairline">
              {metric("Website visits per 1,000 comment/post views", overview.textRate, "tracking-visits-per-1k")}
              {metric("Shortform website visits per 10,000 views", overview.shortformRate10k, "tracking-shortform-per-1k")}
              {metric("Longform website visits per 1,000 views", overview.longformRate, "tracking-longform-per-1k")}
              {metric("Total website visits", overview.visits, "tracking-total-visits", "", true)}
            </div>
          </div>

          <div data-testid="tracking-overview-downloads">
            <h4 className="label-mono text-muted mb-3">Book downloads</h4>
            <div className="border-t border-hairline divide-y divide-hairline">
              {metric("Book downloads per 1,000 views", overview.downloadRate, "tracking-downloads-per-1k")}
              {metric("Book downloads per content posted", overview.downloadsPerContent, "tracking-downloads-per-content")}
              {metric("Content posted per book download", overview.contentPerDownload, "tracking-content-per-download")}
              {metric("Total book downloads", overview.downloads, "tracking-total-downloads", "", true)}
            </div>

            <div className="mt-6 border-t border-hairline divide-y divide-hairline" data-testid="tracking-downloads-by-platform">
              <div className="py-2 grid gap-2 items-baseline" style={{ gridTemplateColumns: "minmax(120px,1fr) minmax(64px,0.8fr) minmax(64px,0.8fr) minmax(64px,0.8fr) minmax(70px,0.9fr) minmax(70px,0.9fr)" }}>
                <span className="label-mono text-muted">Book downloads by platform</span>
                <span className="label-mono text-muted text-right">Content posted</span>
                <span className="label-mono text-muted text-right">Visits</span>
                <span className="label-mono text-muted text-right">Downloads</span>
                <span className="label-mono text-muted text-right">Per 1,000 visits</span>
                <span className="label-mono text-muted text-right">Per 1,000 views</span>
              </div>
              {downloadsByPlatform.map((d) => (
                <div
                  key={d.slug}
                  className="py-3 grid gap-2 items-baseline"
                  style={{ gridTemplateColumns: "minmax(120px,1fr) minmax(64px,0.8fr) minmax(64px,0.8fr) minmax(64px,0.8fr) minmax(70px,0.9fr) minmax(70px,0.9fr)" }}
                  data-testid={`tracking-downloads-platform-${d.slug}`}
                >
                  <span className="text-sm text-ink flex items-center gap-2">
                    <span style={{ color: PLATFORM_COLORS[d.slug] || undefined }} className="flex items-center">
                      <BrandIcon slug={d.slug} />
                    </span>
                    {cleanName(d.name)}
                  </span>
                  <span className="font-display text-xl text-right" data-testid={`tracking-downloads-content-${d.slug}`}>{d.content > 0 ? d.content.toLocaleString("en-US") : "—"}</span>
                  <span className="font-display text-xl text-right">{d.visits.toLocaleString("en-US")}</span>
                  <span className="font-display text-xl text-right">{d.downloads.toLocaleString("en-US")}</span>
                  <span className="font-display text-xl text-right">{d.ratePerVisits}</span>
                  <span className="font-display text-xl text-right">{d.rate}</span>
                </div>
              ))}
              {(() => {
                const t = downloadsByPlatform.reduce(
                  (a, d) => ({
                    content: a.content + d.content,
                    visits: a.visits + d.visits,
                    downloads: a.downloads + d.downloads,
                    views: a.views + d.views,
                  }),
                  { content: 0, visits: 0, downloads: 0, views: 0 }
                );
                const totalPerVisits = t.visits > 0 ? ((t.downloads / t.visits) * 1000).toFixed(1) : "—";
                const totalPerViews = t.views > 0 ? ((t.downloads / t.views) * 1000).toFixed(1) : "—";
                return (
                  <div
                    className="py-3 grid gap-2 items-baseline border-t-2 border-ink"
                    style={{ gridTemplateColumns: "minmax(120px,1fr) minmax(64px,0.8fr) minmax(64px,0.8fr) minmax(64px,0.8fr) minmax(70px,0.9fr) minmax(70px,0.9fr)" }}
                    data-testid="tracking-downloads-platform-total"
                  >
                    <span className="label-mono text-ink flex items-center">Total</span>
                    <span className="font-display text-xl text-right" data-testid="tracking-downloads-total-content">{t.content > 0 ? t.content.toLocaleString("en-US") : "—"}</span>
                    <span className="font-display text-xl text-right" data-testid="tracking-downloads-total-visits">{t.visits.toLocaleString("en-US")}</span>
                    <span className="font-display text-xl text-right" data-testid="tracking-downloads-total-downloads">{t.downloads.toLocaleString("en-US")}</span>
                    <span className="font-display text-xl text-right" data-testid="tracking-downloads-total-pervisits">{totalPerVisits}</span>
                    <span className="font-display text-xl text-right" data-testid="tracking-downloads-total-perviews">{totalPerViews}</span>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        <div className="mb-10 max-w-2xl" data-testid="tracking-streak">
          <h3 className="label-mono text-muted mb-3">Posting streak</h3>
          <div className="border-t border-hairline divide-y divide-hairline">
            {metric(
              "Consecutive days with content posted, up to today",
              streak.current === 0 ? "—" : `${streak.current} day${streak.current === 1 ? "" : "s"}`,
              "tracking-streak-days"
            )}
            {metric(
              "Longest streak ever",
              streak.longest === 0 ? "—" : `${streak.longest} day${streak.longest === 1 ? "" : "s"}`,
              "tracking-streak-longest"
            )}
          </div>
        </div>

        <div className="mb-10 max-w-3xl" data-testid="tracking-week-on-week">
          {(() => {
            const L = {
              week: { title: "Week on week", cur: "This week", prev: "Last week", noun: "week" },
              month: { title: "Month on month", cur: "This month", prev: "Last month", noun: "month" },
              quarter: { title: "Quarter on quarter", cur: "This quarter", prev: "Last quarter", noun: "quarter" },
            }[wowPeriod];
            return (
          <>
          <div className="flex items-center gap-3 mb-1">
            <h3 className="label-mono text-muted">{L.title}</h3>
            <select
              value={wowPeriod}
              onChange={(e) => setWowPeriod(e.target.value)}
              className="label-mono bg-transparent border-b border-ink py-1 outline-none text-ink cursor-pointer"
              data-testid="tracking-wow-period"
            >
              <option value="week">Week on Week</option>
              <option value="month">Month on Month</option>
              <option value="quarter">Quarter on Quarter</option>
            </select>
          </div>
          <p className="text-sm text-muted mb-3">
            {wowPeriod === "week"
              ? `Monday to Sunday, week of ${fmtUtcDay(weekly.weekStart)}.`
              : `Current ${L.noun} vs previous ${L.noun}, starting ${fmtUtcDay(weekly.weekStart)}.`}{" "}
            Not affected by the date filter. Includes manual adjustments (counted on their set date).
          </p>
          {weekly.cur.content < weekly.prev.content && (
            <p className="text-sm text-red-600 mb-3" data-testid="tracking-quiet-week-warning">
              Content posted is down {(weekly.prev.content - weekly.cur.content).toLocaleString("en-US")} on last {L.noun}.
            </p>
          )}
          <div className="border-t border-hairline divide-y divide-hairline">
            <div className="py-2 grid grid-cols-12 gap-2 items-baseline">
              <span className="col-span-6 label-mono text-muted">Metric</span>
              <span className="col-span-2 label-mono text-muted text-right">{L.cur}</span>
              <span className="col-span-2 label-mono text-muted text-right">{L.prev}</span>
              <span className="col-span-2 label-mono text-muted text-right">Change</span>
            </div>
            {[
              ["Website visits", weekly.cur.visits, weekly.prev.visits, "tracking-week-visits"],
              ["Book downloads", weekly.cur.downloads, weekly.prev.downloads, "tracking-week-downloads"],
              ["Content posted", weekly.cur.content, weekly.prev.content, "tracking-week-content"],
              ...(() => {
                const s = weekly.perPlatform.filter((x) => x.format === "shortform");
                const sum = (key, field) => s.reduce((t, x) => t + x[key][field], 0);
                return [
                  ["Shortform website visits (Shorts, Reels, TikToks)", sum("cur", "visits"), sum("prev", "visits"), "tracking-week-visits-shortform"],
                  ["Shortform content posted (Shorts, Reels, TikToks)", sum("cur", "content"), sum("prev", "content"), "tracking-week-content-shortform"],
                ];
              })(),
              ...weekly.perPlatform.flatMap((x) =>
                x.format === "shortform" || x.cur.visits + x.prev.visits + x.cur.content + x.prev.content === 0
                  ? []
                  : [
                      [`${cleanName(x.name)} website visits`, x.cur.visits, x.prev.visits, `tracking-week-visits-${x.slug}`],
                      ...(x.slug === "google-organic"
                        ? []
                        : [[`${cleanName(x.name)} content posted`, x.cur.content, x.prev.content, `tracking-week-content-${x.slug}`]]),
                    ]
              ),
            ].map(([label, cur, prev, testid]) => {
              const delta = cur - prev;
              return (
                <div key={testid} className="py-3 grid grid-cols-12 gap-2 items-baseline" data-testid={testid} style={testid === "tracking-week-visits-shortform" ? { borderTopWidth: 3, borderTopColor: "#0A0A0A" } : undefined}>
                  <span className="col-span-6 text-sm text-ink">{label}</span>
                  <span className="col-span-2 font-display text-lg text-right">{cur.toLocaleString("en-US")}</span>
                  <span className="col-span-2 font-display text-lg text-right">{prev.toLocaleString("en-US")}</span>
                  <span className={`col-span-2 font-display text-lg text-right ${delta < 0 ? "text-red-600" : delta > 0 ? "text-leaf" : ""}`}>
                    {delta === 0 ? "0" : `${delta > 0 ? "+" : ""}${delta.toLocaleString("en-US")}`}
                  </span>
                </div>
              );
            })}
          </div>
          </>
            );
          })()}
        </div>

        <div className="mb-12 max-w-3xl" data-testid="tracking-weekly-performance">
          <div className="flex items-center gap-3 mb-1">
            <h3 className="label-mono text-muted">Weekly performance</h3>
            <select
              value={chartPeriod}
              onChange={(e) => setChartPeriod(e.target.value)}
              className="label-mono bg-transparent border-b border-ink py-1 outline-none text-ink cursor-pointer"
              data-testid="tracking-chart-period"
            >
              <option value="week">Weekly</option>
              <option value="month">Monthly</option>
              <option value="quarter">Quarterly</option>
            </select>
          </div>
          <p className="text-sm text-muted mb-4">
            Total website visits and book downloads per week, within the selected date range. The dashed line marks the average weekly website visits across the weeks shown. Includes manual adjustments (counted on their set date).
          </p>
          {weeklyChart.rows.length === 0 ? (
            <div className="py-3 text-sm text-muted border-t border-hairline" data-testid="tracking-weekly-performance-empty">
              No data in the selected range.
            </div>
          ) : (
            <>
              <div className="w-full" style={{ height: 320 }} data-testid="tracking-weekly-performance-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={weeklyChart.rows} margin={{ top: 10, right: 16, bottom: 0, left: -8 }}>
                    <CartesianGrid stroke="#E5E5E5" vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: "#525252" }}
                      tickLine={false}
                      axisLine={{ stroke: "#E5E5E5" }}
                    />
                    <YAxis
                      yAxisId="left"
                      tick={{ fontSize: 11, fill: "#525252" }}
                      tickLine={false}
                      axisLine={false}
                      width={48}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tick={{ fontSize: 11, fill: "#059669" }}
                      tickLine={false}
                      axisLine={false}
                      width={40}
                    />
                    <Tooltip
                      contentStyle={{ border: "1px solid #E5E5E5", borderRadius: 0, fontSize: 12 }}
                      labelStyle={{ color: "#0A0A0A", fontWeight: 600 }}
                      formatter={(value, name) => [Number(value).toLocaleString("en-US"), name]}
                      labelFormatter={(l) => (chartPeriod === "week" ? `Week of ${l}` : l)}
                    />
                    <ReferenceLine
                      yAxisId="left"
                      y={weeklyChart.avg}
                      stroke="#525252"
                      strokeDasharray="4 4"
                      label={{
                        value: `Avg visits ${weeklyChart.avg.toLocaleString("en-US")}`,
                        position: "insideTopRight",
                        fill: "#525252",
                        fontSize: 11,
                      }}
                    />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="visits"
                      name="Website visits"
                      stroke="#0A0A0A"
                      strokeWidth={2}
                      dot={{ r: 3, fill: "#0A0A0A" }}
                      activeDot={{ r: 5 }}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="downloads"
                      name="Book downloads"
                      stroke="#059669"
                      strokeWidth={2}
                      dot={{ r: 3, fill: "#059669" }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="flex gap-6 mt-3">
                <span className="flex items-center gap-2 label-mono text-muted" data-testid="tracking-weekly-legend-visits">
                  <span className="inline-block w-4 h-0.5" style={{ background: "#0A0A0A" }} />
                  Website visits
                </span>
                <span className="flex items-center gap-2 label-mono text-muted" data-testid="tracking-weekly-legend-downloads">
                  <span className="inline-block w-4 h-0.5" style={{ background: "#059669" }} />
                  Book downloads
                </span>
              </div>
            </>
          )}
        </div>

        <div className="mb-12 max-w-3xl" data-testid="tracking-platform-distribution">
          <div className="flex items-center gap-3 mb-1">
            <h3 className="label-mono text-muted">Platform distribution</h3>
            <select
              value={distPeriod}
              onChange={(e) => setDistPeriod(e.target.value)}
              className="label-mono bg-transparent border-b border-ink py-1 outline-none text-ink cursor-pointer"
              data-testid="tracking-dist-period"
            >
              <option value="total">Total</option>
              <option value="week">Weekly</option>
              <option value="month">Monthly</option>
              <option value="year">Yearly</option>
            </select>
          </div>
          <p className="text-sm text-muted mb-4">
            {distPeriod === "total"
              ? "Each source's share of total website visits and book downloads across all available data. Includes manual adjustments (counted on their set date)."
              : `Each source's share of website visits and book downloads for the current ${distPeriod === "week" ? "week" : distPeriod === "month" ? "month" : "year"}. Includes manual adjustments (counted on their set date).`}
          </p>
          {platformDistribution.items.length === 0 ? (
            <div className="py-3 text-sm text-muted border-t border-hairline" data-testid="tracking-platform-distribution-empty">
              No platform data in the selected range.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-6">
              {platformDistribution.items.map((it) => {
                const cfg = DISTRO_COLORS[it.slug] || { type: "solid", color: "#0A0A0A" };
                const visitPct = platformDistribution.totalVisits > 0 ? (it.visits / platformDistribution.totalVisits) * 100 : 0;
                const dlPct = platformDistribution.totalDownloads > 0 ? (it.downloads / platformDistribution.totalDownloads) * 100 : 0;
                return (
                  <div key={it.slug} data-testid={`tracking-distro-${it.slug}`}>
                    <div className="text-sm text-ink font-semibold mb-2">{cleanName(it.name)}</div>
                    <div className="flex gap-4">
                      <div className="flex flex-col items-center gap-1">
                        <DistroDonut pct={visitPct} cfg={cfg} gid={`distro-${it.slug}-visits`} testid={`tracking-distro-visits-${it.slug}`} />
                        <span className="label-mono text-muted text-[10px]">Visits</span>
                      </div>
                      <div className="flex flex-col items-center gap-1">
                        <DistroDonut pct={dlPct} cfg={cfg} gid={`distro-${it.slug}-downloads`} testid={`tracking-distro-downloads-${it.slug}`} />
                        <span className="label-mono text-muted text-[10px]">Downloads</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="mb-12 max-w-3xl" data-testid="tracking-platform-share">
          <div className="flex items-center gap-3 mb-1">
            <h3 className="label-mono text-muted">Platform share over time</h3>
            <select
              value={shareMetric}
              onChange={(e) => setShareMetric(e.target.value)}
              className="label-mono bg-transparent border-b border-ink py-1 outline-none text-ink cursor-pointer"
              data-testid="tracking-share-metric"
            >
              <option value="visits">Website visits</option>
              <option value="downloads">Book downloads</option>
              <option value="content">Content posted</option>
            </select>
          </div>
          <p className="text-sm text-muted mb-4">
            Each platform's weekly share of {shareMetric === "visits" ? "website visits" : shareMetric === "downloads" ? "book downloads" : "original content posted"}, within the selected date range. Every week totals 100%. Includes manual adjustments (counted on their set date).
          </p>
          {platformShareSeries.rows.length === 0 ? (
            <div className="py-3 text-sm text-muted border-t border-hairline" data-testid="tracking-platform-share-empty">
              No platform data in the selected range.
            </div>
          ) : (
            <>
              <div className="w-full" style={{ height: 300 }} data-testid="tracking-platform-share-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={platformShareSeries.rows} margin={{ top: 10, right: 16, bottom: 0, left: 4 }}>
                    <defs>
                      <linearGradient id="areaGrad-tiktok" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#25F4EE" />
                        <stop offset="100%" stopColor="#FE2C55" />
                      </linearGradient>
                      <linearGradient id="areaGrad-instagram" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#FEDA75" />
                        <stop offset="35%" stopColor="#FA7E1E" />
                        <stop offset="70%" stopColor="#D62976" />
                        <stop offset="100%" stopColor="#962FBF" />
                      </linearGradient>
                      <linearGradient id="areaGrad-google-organic" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#4285F4" />
                        <stop offset="50%" stopColor="#34A853" />
                        <stop offset="100%" stopColor="#FBBC05" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#E5E5E5" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#525252" }} tickLine={false} axisLine={{ stroke: "#E5E5E5" }} />
                    <YAxis
                      domain={[0, 100]}
                      tickFormatter={(v) => `${Math.round(v)}%`}
                      tick={{ fontSize: 11, fill: "#525252" }}
                      tickLine={false}
                      axisLine={false}
                      width={52}
                    />
                    <Tooltip content={<ShareTooltip nameMap={platformShareSeries.nameMap} />} />
                    {platformShareSeries.slugs.map((sl) => (
                      <Area
                        key={sl}
                        type="monotone"
                        dataKey={sl}
                        name={platformShareSeries.nameMap[sl]}
                        stackId="share"
                        stroke="#ffffff"
                        strokeWidth={0.5}
                        fill={shareFillFor(sl)}
                        fillOpacity={1}
                        isAnimationActive={false}
                      />
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-2 mt-3">
                {platformShareSeries.slugs.map((sl) => (
                  <span key={sl} className="flex items-center gap-2 label-mono text-muted" data-testid={`tracking-share-legend-${sl}`}>
                    <span className="inline-block w-3 h-3" style={{ background: SHARE_REP_COLOR[sl] }} />
                    {platformShareSeries.nameMap[sl]}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="mb-12 max-w-3xl" data-testid="tracking-conversion-leaderboard">
          <h3 className="label-mono text-muted mb-1">Conversion leaderboard</h3>
          <p className="text-sm text-muted mb-4">
            Sources ranked by book downloads per 1,000 website visits, within the selected date range. Highest-converting channel first. Includes manual adjustments (counted on their set date).
          </p>
          {conversionLeaderboard.maxRate === 0 ? (
            <div className="py-3 text-sm text-muted border-t border-hairline" data-testid="tracking-conversion-leaderboard-empty">
              No visits in the selected range yet.
            </div>
          ) : (
            <div className="border-t border-hairline divide-y divide-hairline">
              {conversionLeaderboard.ranked.map((x, i) => {
                const isTop = i === 0 && x.rate !== null && x.rate > 0;
                const pct = conversionLeaderboard.maxRate > 0 && x.rate !== null ? (x.rate / conversionLeaderboard.maxRate) * 100 : 0;
                return (
                  <div key={x.slug} className="py-3" data-testid={`tracking-conv-rank-${x.slug}`}>
                    <div className="flex items-baseline justify-between gap-4 mb-1.5">
                      <span className="text-sm text-ink flex items-center gap-2">
                        <span className="label-mono text-muted w-5 text-right">{i + 1}</span>
                        <span style={{ color: SHARE_REP_COLOR[x.slug] || undefined }} className="flex items-center">
                          <BrandIcon slug={x.slug} />
                        </span>
                        {x.name}
                        {isTop && <span className="label-mono text-leaf ml-1">Best</span>}
                      </span>
                      <span className={`font-display text-xl ${isTop ? "text-leaf" : ""}`}>
                        {x.rate === null ? "—" : x.rate.toFixed(1)}
                      </span>
                    </div>
                    <div className="h-2 bg-surface" style={{ marginLeft: "1.75rem" }}>
                      <div className="h-2" style={{ width: `${pct}%`, background: SHARE_REP_COLOR[x.slug] || "#0A0A0A" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="mb-12 max-w-5xl" data-testid="tracking-monthly">
          {(() => {
            const isYear = snapshotPeriod === "year";
            const rows = isYear ? yearly : monthly;
            const colLabel = isYear ? "Year" : "Month";
            const rowLabel = (key) =>
              isYear ? key : new Date(`${key}-01T00:00:00Z`).toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" });
            return (
          <>
          <div className="flex items-center gap-3 mb-1">
            <h3 className="label-mono text-muted">Monthly snapshot</h3>
            <select
              value={snapshotPeriod}
              onChange={(e) => setSnapshotPeriod(e.target.value)}
              className="label-mono bg-transparent border-b border-ink py-1 outline-none text-ink cursor-pointer"
              data-testid="tracking-snapshot-period"
            >
              <option value="month">Monthly</option>
              <option value="year">Yearly</option>
            </select>
          </div>
          <p className="text-sm text-muted mb-3">
            Every {isYear ? "year" : "month"} with data, newest first. Not affected by the date filter. Includes manual adjustments (counted on their set date).
          </p>
          <div className="border-t border-hairline divide-y divide-hairline">
            <div className="py-2 grid gap-4 items-baseline" style={{ gridTemplateColumns: "minmax(110px, 1.4fr) minmax(66px, 1fr) minmax(54px, 1fr) minmax(58px, 1fr) minmax(90px, 1fr) minmax(160px, 1.4fr) minmax(160px, 1.4fr) minmax(135px, 1.2fr)" }}>
              <span className="label-mono text-muted whitespace-nowrap">{colLabel}</span>
              <span className="label-mono text-muted text-right whitespace-nowrap">Content</span>
              <span className="label-mono text-muted text-right whitespace-nowrap">Views</span>
              <span className="label-mono text-muted text-right whitespace-nowrap">Visits</span>
              <span className="label-mono text-muted text-right whitespace-nowrap">Downloads</span>
              <span className="label-mono text-muted text-right whitespace-nowrap">Visits / 1k views</span>
              <span className="label-mono text-muted text-right whitespace-nowrap pl-3" style={{ borderLeftWidth: 3, borderLeftColor: "#0A0A0A" }}>Downloads vs prev</span>
              <span className="label-mono text-muted text-right whitespace-nowrap">Visits vs prev</span>
            </div>
            {rows.length === 0 ? (
              <div className="py-3 text-sm text-muted" data-testid="tracking-monthly-empty">No entries yet.</div>
            ) : (
              rows.map(([ym, m], i) => {
                const prev = rows[i + 1];
                const delta = prev ? m.visits - prev[1].visits : null;
                const dlDelta = prev ? m.downloads - prev[1].downloads : null;
                return (
                <div key={ym} className="py-3 grid gap-4 items-baseline" style={{ gridTemplateColumns: "minmax(110px, 1.4fr) minmax(66px, 1fr) minmax(54px, 1fr) minmax(58px, 1fr) minmax(90px, 1fr) minmax(160px, 1.4fr) minmax(160px, 1.4fr) minmax(135px, 1.2fr)" }} data-testid={`tracking-month-${ym}`}>
                  <span className="text-sm text-ink">
                    {rowLabel(ym)}
                  </span>
                  <span className="font-display text-lg text-right">{m.content.toLocaleString("en-US")}</span>
                  <span className="font-display text-lg text-right">{m.views.toLocaleString("en-US")}</span>
                  <span className="font-display text-lg text-right">{m.visits.toLocaleString("en-US")}</span>
                  <span className="font-display text-lg text-right">{m.downloads.toLocaleString("en-US")}</span>
                  <span className="font-display text-lg text-right">
                    {m.views > 0 ? ((m.visits / m.views) * 1000).toFixed(1) : "—"}
                  </span>
                  <span className={`font-display text-lg text-right pl-3 ${dlDelta !== null && dlDelta > 0 ? "text-leaf" : dlDelta !== null && dlDelta < 0 ? "text-red-600" : ""}`} style={{ borderLeftWidth: 3, borderLeftColor: "#0A0A0A" }} data-testid={`tracking-month-dl-change-${ym}`}>
                    {dlDelta === null || dlDelta === 0 ? "0" : `${dlDelta > 0 ? "+" : ""}${dlDelta.toLocaleString("en-US")}`}
                  </span>
                  <span className={`font-display text-lg text-right ${delta !== null && delta > 0 ? "text-leaf" : delta !== null && delta < 0 ? "text-red-600" : ""}`} data-testid={`tracking-month-change-${ym}`}>
                    {delta === null || delta === 0 ? "0" : `${delta > 0 ? "+" : ""}${delta.toLocaleString("en-US")}`}
                  </span>
                </div>
                );
              })
            )}
          </div>
          </>
            );
          })()}
        </div>


        {attributed.map((p) => {
          const rows = rowsFor(p.id);
          const totals = totalsFor(p, rows);
          const displayRows = rows.filter((r) => !r._adj);
          const bench = benchmarkFor(p, rows);
          const n = p.columns.length;
          const isCollapsed = collapsed[p.id] !== undefined ? collapsed[p.id] : Boolean(p.collapsed);
          const totalPages = Math.max(1, Math.ceil(displayRows.length / ROWS_PER_PAGE));
          const page = Math.min(Math.max(1, pages[p.id] || 1), totalPages);
          const pageRows = displayRows.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);
          const bestDay = (() => {
            const vk = visitKeysOf(p);
            let best = null;
            let worst = null;
            displayRows.forEach((r) => {
              const v = vk.reduce((s, k) => s + num(r.values[k]), 0);
              if (v > 0 && (!best || v > best.visits)) best = { date: r.date, visits: v };
              if (!worst || v < worst.visits) worst = { date: r.date, visits: v };
            });
            return { best, worst };
          })();
          const shareOfVisits = (() => {
            const pVisits = visitKeysOf(p).reduce((t, k) => t + rows.reduce((s, r) => s + num(r.values[k]), 0), 0);
            return overview.visitsNum > 0 ? `${((pVisits / overview.visitsNum) * 100).toFixed(1)}%` : "—";
          })();
          const pContentTotal = p.columns
            .filter((c) => !c.computed && CONTENT_KEYS.includes(c.key))
            .reduce((t, c) => t + rows.reduce((s, r) => s + num(r.values[c.key]), 0), 0);
          const pDownloadsPerContent = pContentTotal > 0
            ? (rows.reduce((s, r) => s + num(r.values.book_downloads), 0) / pContentTotal).toFixed(2)
            : "—";
          const avgTrend = (() => {
            const comp = p.columns.find((c) => c.computed);
            if (!comp) return null;
            const now = new Date();
            const p2 = (n) => String(n).padStart(2, "0");
            const curYm = `${now.getFullYear()}-${p2(now.getMonth() + 1)}`;
            const prevD = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const prevYm = `${prevD.getFullYear()}-${p2(prevD.getMonth() + 1)}`;
            const calc = (ym) => {
              const rs = entries.filter((e) => e.platform_id === p.id && e.date.slice(0, 7) === ym);
              const nSum = comp.computed.numerator.reduce((t, k) => t + rs.reduce((s, r) => s + num(r.values[k]), 0), 0);
              const dSum = comp.computed.denominator.reduce((t, k) => t + rs.reduce((s, r) => s + num(r.values[k]), 0), 0);
              return dSum > 0 ? nSum / dSum : null;
            };
            return { label: comp.label, cur: calc(curYm), prev: calc(prevYm) };
          })();
          const measurePending = MEASURE_PAIRS[p.slug]
            ? entries.filter((e) => e.platform_id === p.id && rowNeedsMeasure(p.slug, e.values, e.date)).length
            : 0;
          return (
            <div key={p.id} className="mb-12" data-testid={`tracking-platform-${p.slug}`}>
              <div className="border-t-2 border-ink mb-8" data-testid={`tracking-divider-${p.slug}`} />
              <div className="flex items-center justify-between gap-4 mb-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      const next = !isCollapsed;
                      setCollapsed((prev) => ({ ...prev, [p.id]: next }));
                      call("patch", `/admin/tracking/platforms/${p.id}/collapsed`, { collapsed: next });
                    }}
                    className="label-mono border border-hairline text-ink px-2 py-0.5 hover:bg-ink hover:text-white transition-colors"
                    data-testid={`tracking-toggle-${p.slug}`}
                  >
                    {isCollapsed ? "Expand" : "Collapse"}
                  </button>
                {renaming === p.id ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      className="bg-transparent border-b border-ink py-1 outline-none text-ink"
                      data-testid={`tracking-rename-input-${p.slug}`}
                      autoFocus
                    />
                    <button onClick={() => saveRename(p)} className={btn} data-testid={`tracking-rename-save-${p.slug}`}>Save</button>
                    <button onClick={() => { setRenaming(null); setRenameValue(""); }} className="label-mono text-muted underline hover:text-ink" data-testid={`tracking-rename-cancel-${p.slug}`}>
                      Cancel
                    </button>
                  </div>
                ) : (
                  <h3
                    className="label-mono text-muted"
                    style={p.slug === "google-organic" ? { color: "#059669", fontWeight: 700 } : PLATFORM_COLORS[p.slug] ? { color: PLATFORM_COLORS[p.slug], fontWeight: 700 } : undefined}
                    data-testid={`tracking-platform-name-${p.slug}`}
                  >
                    {p.name}
                  </h3>
                )}
                {measurePending > 0 && (
                  <span
                    className="label-mono inline-flex items-center gap-1.5 border border-red-300 text-red-700 bg-red-50 px-2 py-0.5 whitespace-nowrap"
                    title="Dates where content was posted but the 3-day views/impressions haven't been entered yet"
                    data-testid={`tracking-measure-pending-${p.slug}`}
                  >
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-600" />
                    {measurePending} date{measurePending === 1 ? " needs" : "s need"} 3-day figures
                  </span>
                )}
                </div>
                {p.type === "custom" && renaming !== p.id && (
                  <div className="flex items-center gap-2">
                    {deletePlatform === p.id ? (
                      <>
                        <span className="label-mono text-muted">
                          Delete {p.name} and its {entries.filter((e) => e.platform_id === p.id).length} row
                          {entries.filter((e) => e.platform_id === p.id).length === 1 ? "" : "s"}?
                        </span>
                        <button onClick={confirmDeletePlatform} className="label-mono bg-red-600 text-white px-3 py-1.5 hover:bg-red-700 transition-colors" data-testid={`tracking-platform-delete-confirm-${p.slug}`}>
                          Confirm
                        </button>
                        <button onClick={() => setDeletePlatform(null)} className={btn} data-testid={`tracking-platform-delete-cancel-${p.slug}`}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => { setRenaming(p.id); setRenameValue(p.name); }} className={btn} data-testid={`tracking-platform-rename-${p.slug}`}>
                          Rename
                        </button>
                        <button onClick={() => setDeletePlatform(p.id)} className={btnDanger} data-testid={`tracking-platform-delete-${p.slug}`}>
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>

              {HOW_TO_COUNT[p.slug] && (
                <div className="mb-4 max-w-3xl" data-testid={`tracking-how-to-count-${p.slug}`}>
                  <button
                    type="button"
                    onClick={() => setHowToOpen((prev) => ({ ...prev, [p.id]: !prev[p.id] }))}
                    className="label-mono text-muted hover:text-ink transition-colors"
                    data-testid={`tracking-how-to-count-toggle-${p.slug}`}
                  >
                    How to count
                  </button>
                  {howToOpen[p.id] && (
                    <div className="mt-2" data-testid={`tracking-how-to-count-text-${p.slug}`}>
                      {HOW_TO_COUNT[p.slug].map((line, i) => (
                        <div key={i} className="flex gap-2 text-sm text-muted leading-relaxed">
                          <span aria-hidden="true">•</span>
                          <span>{line}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="mb-4 flex flex-wrap gap-x-8 gap-y-2 items-baseline" data-testid={`tracking-summary-${p.slug}`}>
                {p.columns.map((c) => (
                  <span key={c.key} className="flex items-baseline gap-2 whitespace-nowrap leading-6">
                    <span className="label-mono text-muted">{c.label}</span>
                    <span className="font-display text-lg" data-testid={`tracking-summary-${p.slug}-${c.key}`}>{totals[c.key]}</span>
                  </span>
                ))}
              </div>

              <div className="mb-4 max-w-3xl" data-testid={`tracking-insights-${p.slug}`}>
                <button
                  type="button"
                  onClick={() => setInsightsOpen((prev) => ({ ...prev, [p.id]: !prev[p.id] }))}
                  className="label-mono text-muted hover:text-ink transition-colors"
                  data-testid={`tracking-insights-toggle-${p.slug}`}
                >
                  Insights
                </button>
                {insightsOpen[p.id] && (
                  <div className="mt-2 max-w-2xl border-t border-hairline divide-y divide-hairline" data-testid={`tracking-insights-body-${p.slug}`}>
                    {[
                      [
                        "Best day",
                        bestDay.best
                          ? `${fmtUtcDay(bestDay.best.date)} · ${bestDay.best.visits.toLocaleString("en-US")} website visit${bestDay.best.visits === 1 ? "" : "s"}`
                          : "—",
                        `tracking-best-day-${p.slug}`,
                        "",
                      ],
                      [
                        "Quietest day",
                        bestDay.worst
                          ? `${fmtUtcDay(bestDay.worst.date)} · ${bestDay.worst.visits.toLocaleString("en-US")} website visit${bestDay.worst.visits === 1 ? "" : "s"}`
                          : "—",
                        `tracking-quietest-day-${p.slug}`,
                        "",
                      ],
                      ["Share of website visits", shareOfVisits, `tracking-share-${p.slug}`, ""],
                      ["Book downloads per content", pDownloadsPerContent, `tracking-downloads-per-content-${p.slug}`, ""],
                      ...(avgTrend
                        ? [
                            [
                              `${avgTrend.label} this month`,
                              avgTrend.cur === null ? "—" : oneDec(avgTrend.cur),
                              `tracking-avg-trend-${p.slug}`,
                              avgTrend.cur !== null && avgTrend.prev !== null && avgTrend.cur < avgTrend.prev ? "text-red-600" : "",
                            ],
                            [
                              `${avgTrend.label} last month`,
                              avgTrend.prev === null ? "—" : oneDec(avgTrend.prev),
                              `tracking-avg-trend-prev-${p.slug}`,
                              "",
                            ],
                          ]
                        : []),
                    ].map(([label, value, testid, valueClass]) => (
                      <div
                        key={testid}
                        className="py-1.5 grid gap-2 items-baseline"
                        style={{ gridTemplateColumns: "minmax(240px, 300px) 1fr" }}
                        data-testid={testid}
                      >
                        <span className="label-mono text-muted">{label}</span>
                        <span className={`label-mono text-ink ${valueClass}`}>{value}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {!isCollapsed && (
                <>
              <div className="border-t border-hairline divide-y divide-hairline">
                <div className="py-2 grid gap-2 items-end" style={gridStyle(n)}>
                  <span className="label-mono text-muted">Date</span>
                  {p.columns.map((c) => (
                    <span key={c.key} className="label-mono text-muted text-right leading-5">{c.label}</span>
                  ))}
                  <span />
                </div>

                {rows.length === 0 ? (
                  <div className="py-3 text-sm text-muted" data-testid={`tracking-empty-${p.slug}`}>
                    No entries for the selected period.
                  </div>
                ) : (
                  <>
                    {pageRows.map((r) => {
                      const needsMeasure = rowNeedsMeasure(p.slug, r.values, r.date);
                      return (
                      <div key={r.id} className="py-3 grid gap-2 items-baseline" style={gridStyle(n)} data-testid={`tracking-row-${p.slug}-${r.date}`}>
                        <span className="text-sm flex items-center gap-1.5">
                          {needsMeasure && (
                            <span
                              className="inline-block w-1.5 h-1.5 rounded-full bg-red-600 shrink-0"
                              title="3-day views/impressions not entered yet"
                              data-testid={`tracking-measure-flag-${p.slug}-${r.date}`}
                            />
                          )}
                          <span className={needsMeasure ? "text-red-600" : "text-ink"}>{fmtUtcDay(r.date)}</span>
                        </span>
                        {p.columns.map((c) => {
                          if (c.computed) {
                            const nSum = c.computed.numerator.reduce((s, k) => s + num(r.values[k]), 0);
                            const dSum = c.computed.denominator.reduce((s, k) => s + num(r.values[k]), 0);
                            return (
                              <span
                                key={c.key}
                                className="font-display text-lg text-right text-muted"
                                data-testid={`tracking-cell-${p.slug}-${r.date}-${c.key}`}
                              >
                                {dSum > 0
                                  ? (nSum / dSum).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 })
                                  : "—"}
                              </span>
                            );
                          }
                          const dk = `${r.id}:${c.key}`;
                          const stored = r.values[c.key];
                          const value = drafts[dk] !== undefined ? drafts[dk] : (stored === undefined || stored === null ? "" : String(stored));
                          return (
                            <input
                              key={c.key}
                              type="number"
                              value={value}
                              onChange={(e) => setDrafts((prev) => ({ ...prev, [dk]: e.target.value }))}
                              onBlur={() => {
                                if (drafts[dk] === undefined) return;
                                const before = stored === undefined || stored === null ? "" : String(stored);
                                if (drafts[dk] === before) {
                                  setDrafts((prev) => { const next = { ...prev }; delete next[dk]; return next; });
                                  return;
                                }
                                saveCell(p, r, c.key, drafts[dk]);
                              }}
                              className="w-full bg-transparent border-b border-transparent hover:border-hairline focus:border-ink outline-none font-display text-lg text-right"
                              data-testid={`tracking-cell-${p.slug}-${r.date}-${c.key}`}
                            />
                          );
                        })}
                        <span className="text-right">
                          {r._acq ? (
                            <span className="label-mono text-muted/60" data-testid={`tracking-row-auto-${p.slug}-${r.date}`}>auto</span>
                          ) : deleteRow === r.id ? (
                            <span className="flex items-baseline justify-end gap-2">
                              <button onClick={confirmDeleteRow} className="label-mono bg-red-600 text-white px-2 py-0.5 hover:bg-red-700 transition-colors" data-testid={`tracking-row-delete-confirm-${p.slug}-${r.date}`}>
                                Confirm
                              </button>
                              <button onClick={() => setDeleteRow(null)} className="label-mono border border-hairline text-ink px-2 py-0.5 hover:bg-surface transition-colors" data-testid={`tracking-row-delete-cancel-${p.slug}-${r.date}`}>
                                Cancel
                              </button>
                            </span>
                          ) : (
                            <button onClick={() => setDeleteRow(r.id)} className="label-mono border border-red-200 text-red-600 px-2 py-0.5 hover:bg-red-600 hover:text-white transition-colors" data-testid={`tracking-row-delete-${p.slug}-${r.date}`}>
                              Delete
                            </button>
                          )}
                        </span>
                      </div>
                      );
                    })}
                    {p.slug === "google-organic" && (() => {
                      const adj = adjFor(p.slug);
                      const inR = adj.date ? inRange(adj.date) : true;
                      const prev = adj.prev;
                      const canRestore = prev && (prev.visits !== 0 || prev.downloads !== 0)
                        && !(prev.visits === adj.visits && prev.downloads === adj.downloads);
                      const adjDk = (key) => `adj:${p.slug}:${key}`;
                      const adjVal = (key, stored) => (drafts[adjDk(key)] !== undefined ? drafts[adjDk(key)] : String(stored));
                      const saveAdjCell = (key, current) => {
                        const raw = drafts[adjDk(key)];
                        if (raw === undefined) return;
                        setDrafts((prev) => { const nx = { ...prev }; delete nx[adjDk(key)]; return nx; });
                        const parsed = raw.trim() === "" || raw.trim() === "-" ? 0 : Math.trunc(Number(raw));
                        if (!Number.isFinite(parsed) || parsed === current) return;
                        saveAdj(p.slug, { [key === "website_visits" ? "visits" : "downloads"]: parsed });
                      };
                      return (
                      <div className={`py-3 grid gap-2 items-baseline border-y ${inR ? "bg-amber-50 border-amber-300" : "bg-surface border-hairline opacity-70"}`} style={gridStyle(n)} data-testid={`tracking-adjustment-${p.slug}`}>
                        <span className="flex flex-col gap-1">
                          <span className={`label-mono italic ${inR ? "text-amber-700" : "text-muted"}`}>Manual adjustment</span>
                          <input
                            type="date"
                            value={adj.date || ""}
                            onChange={(e) => e.target.value && saveAdj(p.slug, { date: e.target.value })}
                            className="label-mono bg-transparent border-b border-amber-400 focus:border-amber-700 outline-none text-amber-700 w-[9.5rem]"
                            data-testid={`tracking-adjustment-date-${p.slug}`}
                          />
                          {!inR && <span className="label-mono text-muted text-xs" data-testid={`tracking-adjustment-outside-${p.slug}`}>outside selected range — not in totals</span>}
                        </span>
                        {p.columns.map((c) => {
                          if (c.key !== "website_visits" && c.key !== "book_downloads") {
                            return <span key={c.key} className="font-display text-lg text-right text-muted">—</span>;
                          }
                          const current = c.key === "website_visits" ? adj.visits : adj.downloads;
                          return (
                            <input
                              key={c.key}
                              type="number"
                              value={adjVal(c.key, current)}
                              onChange={(e) => setDrafts((prev) => ({ ...prev, [adjDk(c.key)]: e.target.value }))}
                              onBlur={() => saveAdjCell(c.key, current)}
                              className={`w-full bg-transparent border-b border-amber-400 focus:border-amber-700 outline-none font-display text-lg text-right ${inR ? "text-amber-700" : "text-muted"}`}
                              data-testid={`tracking-adjustment-${p.slug}-${c.key}`}
                            />
                          );
                        })}
                        <span className="text-right flex flex-col items-end gap-1">
                          <button
                            onClick={() => saveAdj(p.slug, { visits: 0, downloads: 0 })}
                            className="label-mono border border-amber-400 text-amber-700 px-2 py-0.5 hover:bg-amber-600 hover:text-white transition-colors"
                            data-testid={`tracking-adjustment-clear-${p.slug}`}
                          >
                            Clear
                          </button>
                          {canRestore && (
                            <button
                              onClick={() => restorePrev(p.slug)}
                              title={`Restore ${prev.visits >= 0 ? "+" : ""}${prev.visits} visits / ${prev.downloads >= 0 ? "+" : ""}${prev.downloads} downloads`}
                              className="label-mono border border-hairline text-ink px-2 py-0.5 hover:bg-ink hover:text-white transition-colors whitespace-nowrap"
                              data-testid={`tracking-adjustment-restore-${p.slug}`}
                            >
                              Restore previous
                            </button>
                          )}
                        </span>
                      </div>
                      );
                    })()}
                    <div className="py-3 grid gap-2 items-baseline" style={gridStyle(n)} data-testid={`tracking-total-${p.slug}`}>
                      <span className="label-mono text-muted">Total</span>
                      {p.columns.map((c) => (
                        <span key={c.key} className="font-display text-lg text-right" data-testid={`tracking-total-${p.slug}-${c.key}`}>
                          {totals[c.key]}
                        </span>
                      ))}
                      <span />
                    </div>
                  </>
                )}
              </div>

              {totalPages > 1 && (
                <div className="mt-3 flex items-center gap-3 flex-wrap" data-testid={`tracking-pagination-${p.slug}`}>
                  <button
                    onClick={() => setPages((prev) => ({ ...prev, [p.id]: page - 1 }))}
                    disabled={page <= 1}
                    className="label-mono border border-hairline text-ink px-3 py-1.5 hover:bg-ink hover:text-white transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-ink"
                    data-testid={`tracking-page-prev-${p.slug}`}
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPages((prev) => ({ ...prev, [p.id]: page + 1 }))}
                    disabled={page >= totalPages}
                    className="label-mono border border-hairline text-ink px-3 py-1.5 hover:bg-ink hover:text-white transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-ink"
                    data-testid={`tracking-page-next-${p.slug}`}
                  >
                    Next
                  </button>
                  <span className="label-mono text-muted" data-testid={`tracking-page-status-${p.slug}`}>
                    Page {page} of {totalPages} · {displayRows.length} rows
                  </span>
                </div>
              )}
                </>
              )}

              {bench && (
                <div className="mt-6 max-w-2xl" data-testid={`tracking-benchmark-${p.slug}`}>
                  <div className="flex items-baseline justify-between gap-4 mb-3 flex-wrap">
                    <h4 className={`label-mono ${bench.under ? "text-red-600" : "text-muted"}`}>
                      Benchmark{bench.under ? " — under" : ""}
                    </h4>
                    <label className="label-mono text-muted flex items-center gap-2">
                      % of {bench.sourceLabel}
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.001"
                        value={
                          benchDrafts[p.id] !== undefined
                            ? benchDrafts[p.id]
                            : p.benchmark_pct === null || p.benchmark_pct === undefined
                              ? ""
                              : String(p.benchmark_pct)
                        }
                        onChange={(e) => setBenchDrafts((prev) => ({ ...prev, [p.id]: e.target.value }))}
                        onBlur={() => {
                          if (benchDrafts[p.id] === undefined) return;
                          const before =
                            p.benchmark_pct === null || p.benchmark_pct === undefined ? "" : String(p.benchmark_pct);
                          if (benchDrafts[p.id].trim() === before) {
                            setBenchDrafts((prev) => { const next = { ...prev }; delete next[p.id]; return next; });
                            return;
                          }
                          saveBenchmark(p, benchDrafts[p.id]);
                        }}
                        className="w-24 bg-transparent border-b border-hairline focus:border-ink py-1 outline-none text-ink font-display text-lg text-right"
                        data-testid={`tracking-benchmark-input-${p.slug}`}
                      />
                    </label>
                  </div>
                  <div className="border-t border-hairline divide-y divide-hairline">
                    {metric("Actual website visits", bench.actual.toLocaleString("en-US"), `tracking-benchmark-actual-${p.slug}`)}
                    {metric(
                      "Expected website visits",
                      bench.expected === null ? "—" : oneDec(bench.expected),
                      `tracking-benchmark-expected-${p.slug}`
                    )}
                    {metric(
                      "Difference vs expected",
                      bench.diff === null ? "—" : `${bench.diff > 0 ? "+" : ""}${oneDec(bench.diff)}`,
                      `tracking-benchmark-difference-${p.slug}`,
                      bench.under ? "text-red-600" : ""
                    )}
                    {metric(
                      "Performance vs expected",
                      bench.perf === null ? "—" : `${bench.perf > 0 ? "+" : ""}${oneDec(bench.perf)}%`,
                      `tracking-benchmark-performance-${p.slug}`,
                      bench.perf === null || bench.perf === 0 ? "" : bench.perf > 0 ? "text-green-600" : "text-red-600"
                    )}
                  </div>
                </div>
              )}

              {!isCollapsed && (
              <div className="mt-3 flex items-center gap-3 flex-wrap">
                <input
                  type="date"
                  value={addDates[p.id] || todayStr()}
                  onChange={(e) => setAddDates((prev) => ({ ...prev, [p.id]: e.target.value }))}
                  className="bg-transparent border-b border-hairline focus:border-ink py-1 outline-none text-ink text-sm"
                  data-testid={`tracking-add-date-${p.slug}`}
                />
                <button onClick={() => addRow(p)} className={btn} data-testid={`tracking-add-row-${p.slug}`}>
                  + Add row
                </button>
                {notices[p.id] && (
                  <span className="label-mono text-muted" data-testid={`tracking-notice-${p.slug}`}>{notices[p.id]}</span>
                )}
              </div>
              )}
            </div>
          );
        })}

        {duPlatform && (() => {
          const rows = rowsFor(duId);
          const duVisits = rows.reduce((s, r) => s + num(r.values.website_visits), 0);
          const duDownloads = rows.reduce((s, r) => s + num(r.values.book_downloads), 0);
          return (
            <div className="mb-12" data-testid="tracking-platform-direct-unknown">
              <div className="border-t-2 border-ink mb-6" />
              <div className="flex items-baseline justify-between gap-4 flex-wrap py-3 border-b border-hairline">
                <div>
                  <div className="font-display text-lg text-muted">Unattributed</div>
                  <div className="label-mono text-muted text-xs">excluded from charts</div>
                </div>
                <div className="flex items-baseline gap-8">
                  <span className="text-right">
                    <span className="label-mono text-muted block text-xs">Website visits</span>
                    <span className="font-display text-xl" data-testid="tracking-unattributed-visits">{duVisits.toLocaleString("en-US")}</span>
                  </span>
                  <span className="text-right">
                    <span className="label-mono text-muted block text-xs">Book downloads</span>
                    <span className="font-display text-xl" data-testid="tracking-unattributed-downloads">{duDownloads.toLocaleString("en-US")}</span>
                  </span>
                </div>
              </div>
              <p className="label-mono text-muted text-xs mt-2">Raw Direct / Unknown traffic for {rangeLabel.toLowerCase()}. Not included in any chart, share, leaderboard, weekly or monthly total.</p>
            </div>
          );
        })()}

        <div className="mt-12 border-t border-hairline pt-6">
          {adding ? (
            <div className="flex items-center gap-3 flex-wrap">
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Platform name"
                className="bg-transparent border-b border-ink py-1 outline-none text-ink"
                data-testid="tracking-new-platform-input"
                autoFocus
              />
              <button onClick={createPlatform} className={btn} data-testid="tracking-new-platform-save">Create</button>
              <button onClick={() => { setAdding(false); setNewName(""); setError(""); }} className="label-mono text-muted underline hover:text-ink" data-testid="tracking-new-platform-cancel">
                Cancel
              </button>
            </div>
          ) : (
            <button onClick={() => { setAdding(true); setError(""); }} className={btn} data-testid="tracking-add-platform">
              + Add platform
            </button>
          )}
        </div>
    </div>
  );

  if (embedded) return content;

  return (
    <div className="min-h-screen bg-paper" data-testid="admin-tracking-page">
      <header className="border-b border-hairline sticky top-0 z-10 backdrop-blur-md bg-white/85">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/admin" className="font-display text-lg" style={{ fontWeight: 800 }} data-testid="admin-tracking-back">
              Admin
            </Link>
            <div className="flex items-center gap-1 border border-hairline p-1">
              <span className="label-mono px-4 py-2 bg-ink text-white" data-testid="admin-tracking-nav-active">
                Distribution
              </span>
            </div>
          </div>
          <button onClick={logout} className="label-mono text-muted hover:text-ink transition-colors" data-testid="admin-tracking-logout">
            Logout
          </button>
        </div>
      </header>

      <main className="max-w-screen-xl mx-auto px-6 md:px-12 py-12 md:py-16">{content}</main>
    </div>
  );
}
